(() => {
  const defaultRideState = { id: 'ride', label: 'Ride', text: 'RIDE' };
  const runtimeScriptSrc = document.currentScript?.src || window.location.href;
  const defaultIconStates = [
    { id: 'harley-touring', label: 'Harley Touring' },
    { id: 'harley-sportster', label: 'Harley Sportster' },
    { id: 'harley-trike', label: 'Harley Trike' },
    { id: 'harley-cruiser', label: 'Harley Cruiser' },
  ];
  const configuredIntervalMs = Number(window.parityGarageRideCycleIntervalMs);
  const rideCycleIntervalMs = configuredIntervalMs > 0 ? configuredIntervalMs : 2000;

  const resolveRideCycleImage = (image) => {
    if (!image) return '';

    try {
      return new URL(image, runtimeScriptSrc).href;
    } catch (_error) {
      return image;
    }
  };

  const imageForRideCycleState = (state) => {
    if (state?.image || state?.asset || state?.src) {
      return resolveRideCycleImage(state.image || state.asset || state.src);
    }

    if (state?.text) return '';

    return resolveRideCycleImage(`garage-cycle-${state.id}.png`);
  };

  const normalizeRideCycleStates = (states) => {
    const normalized = [];
    const seen = new Set(['ride']);

    (Array.isArray(states) && states.length ? states : defaultIconStates).forEach((state) => {
      if (!state?.id || seen.has(state.id)) return;

      seen.add(state.id);
      normalized.push({
        id: state.id,
        label: state.label || state.id,
        text: state.text || '',
        image: imageForRideCycleState(state),
      });
    });

    return [defaultRideState, ...normalized];
  };

  const rideCycleStates = normalizeRideCycleStates(window.parityGarageRideCycleStates);

  let stateIndex = 0;
  let timer = null;
  let frame = null;
  let preloadPromise = null;
  let activeRideCycleStates = [defaultRideState];

  const normalizeGarageText = (value) => (value || '').replace(/\s+/g, ' ').trim();
  const emptyGarageAdvantages = [
    'Store your vehicle in the garage',
    'Get products for your vehicle',
    'Easily find the parts you need',
  ];

  const hasSavedGarageVehicle = (button) => {
    const garage = button.closest('#cm-garage, #cm-mobile-garage') || button.closest('.cm_garage');
    const vehicle = Array.from(
      garage?.querySelectorAll('.cm_garageDropdown [data-cm-role="select-vehicle"], .cm_garageDropdown .select-model') || [],
    ).find((item) => normalizeGarageText(item.textContent));

    return Boolean(vehicle);
  };

  const syncGarageDropdownArrow = (button) => {
    const garage = button.closest('#cm-garage, #cm-mobile-garage') || button.closest('.cm_garage');
    const dropdown = garage?.querySelector('.cm_garageDropdown');
    const icon = button.querySelector('.garage-icon-container, .garage-icon');

    if (!dropdown || !icon) return;

    const dropdownRect = dropdown.getBoundingClientRect();
    const iconRect = icon.getBoundingClientRect();

    if (!dropdownRect.width || !iconRect.width) return;

    const arrowX = iconRect.left + (iconRect.width / 2) - dropdownRect.left;
    dropdown.style.setProperty('--cm-garage-arrow-x', `${arrowX}px`);
  };

  const syncOpenGarageDropdownArrows = () => {
    document
      .querySelectorAll('#cm-garage .garage-button, #cm-mobile-garage .garage-button')
      .forEach((button) => {
        const garage = button.closest('#cm-garage, #cm-mobile-garage') || button.closest('.cm_garage');
        const dropdown = garage?.querySelector('.cm_garageDropdown');
        const rect = dropdown?.getBoundingClientRect();
        const style = dropdown ? getComputedStyle(dropdown) : null;

        if (
          dropdown
          && style?.display !== 'none'
          && style?.visibility !== 'hidden'
          && rect?.width
          && rect?.height
        ) {
          syncGarageDropdownArrow(button);
        }
      });
  };

  const syncEmptyGarageDropdown = (button, hasVehicle) => {
    const garage = button.closest('#cm-garage, #cm-mobile-garage') || button.closest('.cm_garage');
    const dropdown = garage?.querySelector('.cm_garageDropdown');

    if (!dropdown) return;

    syncGarageDropdownArrow(button);
    dropdown.classList.toggle('garage-empty-normalized', !hasVehicle);

    if (hasVehicle) {
      dropdown.querySelectorAll('.garage-empty-advantages[data-parity-empty-garage="true"]').forEach((list) => list.remove());
      return;
    }

    if (dropdown.querySelector('.vehicle-advantages')) return;

    const addVehicle = dropdown.querySelector('.add-vehicle, .cm_addButton');
    const content = addVehicle?.closest('.garage-content') || addVehicle?.parentElement || dropdown;

    if (!addVehicle || !content) return;

    const list = document.createElement('ul');
    list.className = 'garage-empty-advantages';
    list.dataset.parityEmptyGarage = 'true';

    emptyGarageAdvantages.forEach((text) => {
      const item = document.createElement('li');
      item.className = 'vehicle-advantages';
      item.textContent = text;
      list.appendChild(item);
    });

    addVehicle.insertAdjacentElement('afterend', list);
  };

  const syncRideCyclePrompt = (button) => {
    const hasVehicle = hasSavedGarageVehicle(button);
    const prefix = button.querySelector('.garage-cycle-prefix');
    const slot = button.querySelector('.garage-cycle-slot');

    button.dataset.garageHasVehicle = hasVehicle ? 'true' : 'false';
    button.setAttribute('aria-label', hasVehicle ? 'Shop your ride' : 'Choose your ride');
    syncEmptyGarageDropdown(button, hasVehicle);

    if (prefix) {
      prefix.textContent = hasVehicle ? 'SHOP YOUR' : 'CHOOSE YOUR';
    }

    if (hasVehicle && slot) {
      slot.dataset.rideCycleState = defaultRideState.id;
      slot.setAttribute('aria-label', defaultRideState.label);
      slot.textContent = defaultRideState.text;
    }

    return hasVehicle;
  };

  const preloadRideCycleIcon = (state) => new Promise((resolve) => {
    if (!state.image || typeof Image !== 'function') {
      resolve(!state.image ? state : null);
      return;
    }

    const image = new Image();
    let settled = false;
    const finish = (ready) => {
      if (settled) return;

      settled = true;
      resolve(ready ? state : null);
    };

    image.decoding = 'async';
    image.onload = () => finish(true);
    image.onerror = () => finish(false);
    image.src = state.image;

    if (image.complete) {
      finish(image.naturalWidth !== 0);
    }
  });

  const preloadRideCycleIcons = () => {
    if (preloadPromise) return preloadPromise;

    const preloadStates = rideCycleStates.filter((state) => state.id !== defaultRideState.id);

    preloadPromise = Promise.all(preloadStates.map(preloadRideCycleIcon)).then((loadedStates) => {
      const loadedIds = new Set(
        loadedStates
          .filter(Boolean)
          .map((state) => state.id),
      );

      activeRideCycleStates = rideCycleStates.filter((state) => (
        state.id === defaultRideState.id || loadedIds.has(state.id)
      ));

      return activeRideCycleStates;
    });

    return preloadPromise;
  };

  const applyRideCycleState = () => {
    if (activeRideCycleStates.length < 2) return;

    const state = activeRideCycleStates[stateIndex % activeRideCycleStates.length];
    const slots = document.querySelectorAll('.garage-cycle-slot');

    slots.forEach((slot) => {
      const button = slot.closest('.garage-button');

      if (button && syncRideCyclePrompt(button)) return;

      slot.dataset.rideCycleState = state.id;
      slot.setAttribute('aria-label', state.label);
      slot.textContent = state.text || '';
    });

    stateIndex += 1;
  };

  const startRideCycleTimer = () => {
    if (timer || !document.querySelector('.garage-cycle-slot') || activeRideCycleStates.length < 2) return;

    const initialSlot = document.querySelector('.garage-cycle-slot');
    const initialStateIndex = activeRideCycleStates.findIndex((state) => state.id === initialSlot?.dataset.rideCycleState);
    if (initialStateIndex >= 0) stateIndex = initialStateIndex + 1;
    timer = window.setInterval(applyRideCycleState, rideCycleIntervalMs);
  };

  const ensureRideCycle = () => {
    document
      .querySelectorAll('#cm-garage .garage-button, #cm-mobile-garage .garage-button')
      .forEach((button) => {
        if (button.dataset.rideCycleReady === 'true') {
          syncRideCyclePrompt(button);
          return;
        }

        button.dataset.rideCycleReady = 'true';

        const label = document.createElement('span');
        label.className = 'garage-cycle-label';
        label.setAttribute('aria-hidden', 'true');
        label.innerHTML = '<span class="garage-cycle-prefix">CHOOSE YOUR</span><span class="garage-cycle-slot" data-ride-cycle-state="ride">RIDE</span>';
        button.appendChild(label);

        syncRideCyclePrompt(button);
      });

    if (document.querySelector('.garage-cycle-slot')) {
      preloadRideCycleIcons().then(startRideCycleTimer);
      startRideCycleTimer();
    }
  };

  const scheduleRideCycle = () => {
    if (frame) return;

    frame = window.requestAnimationFrame(() => {
      frame = null;
      ensureRideCycle();
    });
  };

  scheduleRideCycle();
  document.addEventListener('DOMContentLoaded', scheduleRideCycle);
  window.addEventListener('load', scheduleRideCycle);
  window.addEventListener('resize', syncOpenGarageDropdownArrows);
  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;

    const button = event.target.closest('#cm-garage .garage-button, #cm-mobile-garage .garage-button');

    if (!button) return;

    window.requestAnimationFrame(() => syncGarageDropdownArrow(button));
    window.setTimeout(() => syncGarageDropdownArrow(button), 50);
  });

  if ('MutationObserver' in window) {
    new MutationObserver(scheduleRideCycle).observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  }
})();
